/**
 * ClinicalHUD3 Teaching one-time learner onboarding.
 * US clinician checkbox is optional for entry; NPI is optional format-only.
 */
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { isValidNpiFormat } from "../npi";
import { saveLearnerProfile } from "../clinicalHud3LiveCallables";

/**
 * @param {{ refresh?: () => void, productTitle?: string, productTagline?: string }} props
 */
export default function ClinicalHud3LearnerOnboarding({
  refresh,
  productTitle,
  productTagline,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      usPracticeAttested: false,
      npi: "",
    },
    mode: "onChange",
  });

  const npiValue = watch("npi") || "";
  const npiTrimmed = npiValue.trim();
  const npiInvalid =
    npiTrimmed.length > 0 && !isValidNpiFormat(npiTrimmed);

  const onSubmit = async (values) => {
    if (submitting || npiInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const npi = (values.npi || "").trim();
      const payload = {
        usPracticeAttested: Boolean(values.usPracticeAttested),
      };
      if (npi) {
        payload.npi = npi;
      }
      await saveLearnerProfile(payload);
      if (typeof refresh === "function") {
        refresh();
      }
    } catch (err) {
      setError(
        err?.message || "Could not save your answer. Please try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", py: 6, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Welcome to {productTitle || "ClinicalHUD 3"} — Teaching mode
      </Typography>
      {productTagline ? (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {productTagline}
        </Typography>
      ) : null}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {productTitle || "ClinicalHUD 3"} is an educational tool that uses
        simulated cases to train medical practitioners.
      </Typography>
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="usPracticeAttested"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(field.value)}
                  onChange={(event) => field.onChange(event.target.checked)}
                  disabled={submitting}
                />
              }
              label="I practice or train as a clinician in the United States"
              sx={{ mb: 2, display: "flex", alignItems: "flex-start" }}
            />
          )}
        />
        <Controller
          name="npi"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="NPI (optional)"
              fullWidth
              disabled={submitting}
              error={npiInvalid}
              helperText={
                npiInvalid
                  ? "Enter a valid 10-digit NPI, or leave blank."
                  : undefined
              }
              sx={{ mb: 2 }}
            />
          )}
        />
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        <LoadingButton
          type="submit"
          loading={submitting}
          disabled={npiInvalid}
        >
          Continue
        </LoadingButton>
      </Box>
    </Box>
  );
}
